import { Component as BaseComponent, h } from 'preact';
import { connect } from 'react-redux';

/* eslint-disable max-len */
class Component extends BaseComponent {
    render() {
        return (
            <section id='grid'>
                <fieldset>
                    <legend>
                        <h2><a href='#basics'>#</a> Grid</h2>
                    </legend>
                    <row>
                        <column>
                            <p>
                                We use a responsive, 12 column,&nbsp;
                                <a
                                    href='https://css-tricks.com/snippets/css/a-guide-to-flexbox/'
                                    target='_blank'
                                    rel='noopener'
                                >flex</a>-based grid system for desiging layouts.
                            </p>
                            <p>
                                A grid is started with a <code className='prettyprint lang-html'>
                                    &lt;row&gt;...&lt;/row&gt;
                                </code> element,
                                followed by one, or more, <code className='prettyprint lang-html'>
                                    &lt;column&gt;...&lt;/column&gt;
                                </code>
                                &nbsp;elements. A row is a&nbsp;
                                <a
                                    href='https://www.w3schools.com/html/html_blocks.asp'
                                    target='_blank'
                                    rel='noopener'
                                >block</a>-level element (full width, new line after). Columns are flex-based, and
                                without any <a href='#options'>options</a> will <b>equally distribute</b> across the
                                row, filling all available width.
                            </p>
                            <small>
                                In the examples on this page, a row is shown in&nbsp;
                                <span className='is-background-cinderella'>pink</span> (with extra padding), columns are
                                shown in <span className='is-background-tahuna-sands'>green</span> (with text centered).
                            </small>
                            <br />
                            <br />
                            Example:
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column&gt;column 2&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column&gt;column 3&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column&gt;column 4&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row>
                                    <column>column 1</column>
                                    <column>column 2</column>
                                    <column>column 3</column>
                                    <column>column 4</column>
                                </row>
                            </div>
                        </column>
                    </row>
                </fieldset>
                <fieldset id='sizes'>
                    <legend>
                        <h2><a href='#sizes'>#</a> Column Sizes</h2>
                    </legend>
                    <row>
                        <column>
                            <p>
                                Adding an <code className='prettyprint'>is-[1 - 12]</code> class to a column defines the
                                width of the column. The number after <code className='prettyprint'>is-</code> is a
                                fraction of 12. So, <code className='prettyprint'>is-1</code> makes the column 1/12 of the
                                width of the row (0.08333%), <code className='prettyprint'>is-5</code> = 5/12 row width
                                (0.41666%), and so on. The column classes can be mixed inside a row, where the widths
                                add up to a maximum of 12.
                            </p>
                            <br />
                            <br />
                            Examples:
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-8"&gt;column 2&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row>
                                    <column className='is-4'>column 1</column>
                                    <column className='is-8'>column 2</column>
                                </row>
                            </div>
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4"&gt;column 2&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4"&gt;column 3&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row>
                                    <column className='is-4'>column 1</column>
                                    <column className='is-4'>column 2</column>
                                    <column className='is-4'>column 3</column>
                                </row>
                            </div>
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-2"&gt;column 2&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-6"&gt;column 3&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row>
                                    <column className='is-4'>column 1</column>
                                    <column className='is-2'>column 2</column>
                                    <column className='is-6'>column 3</column>
                                </row>
                            </div>
                        </column>
                    </row>
                </fieldset>
                <fieldset id='responsive'>
                    <legend>
                        <h2><a href='#responsive'>#</a> Responsive</h2>
                    </legend>
                    <row>
                        <column>
                            <p>
                                Responsive classes can be added to columns as well. The class
                                <code className='prettyprint'>is-8 is-12-mobile</code> will change the width of the column
                                for mobile. The column width will be 8/12 on desktop and tablet, and 12/12 on mobile.
                                Make sense?
                            </p>
                            <p>
                                Available responsive classes:
                                <br />
                                <code className='prettyprint'>is-[1 - 12]-desktop</code>
                                <br />
                                <code className='prettyprint'>is-[1 - 12]-tablet</code>
                                <br />
                                <code className='prettyprint'>is-[1 - 12]-mobile</code>
                                <br />
                            </p>
                            <p>
                                When using <code className='prettyprint'>is-[1 - 12]-[desktop|tablet|mobile]</code>
                                &nbsp;classes, remember to include classes for <i>all</i> devices. Don't only add a
                                class of <code className='prettyprint'>is-8-mobile</code> on a column, as it won't know
                                what to do on non-mobile devices.
                            </p>
                            <small>
                                To see the examples in action, resize your browser window or use your browsers
                                responsive inspector.
                            </small>
                            <br />
                            <br />
                            Examples:
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-8 is-6-mobile"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4 is-6-mobile"&gt;column 2&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row>
                                    <column className='is-8 is-6-mobile'>column 1</column>
                                    <column className='is-4 is-6-mobile'>column 2</column>
                                </row>
                            </div>
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-8-desktop is-7-tablet is-6-mobile"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4-desktop is-5-tablet is-6-mobile"&gt;column 2&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row>
                                    <column className='is-8-desktop is-7-tablet is-6-mobile'>column 1</column>
                                    <column className='is-4-desktop is-5-tablet is-6-mobile'>column 2</column>
                                </row>
                            </div>
                        </column>
                    </row>
                </fieldset>
                <fieldset id='options'>
                    <legend>
                        <h2><a href='#options'>#</a> Options</h2>
                    </legend>
                    <row>
                        <column className='is-4'>
                            <h3><code className='prettyprint'>&lt;row&gt;</code> classes:</h3>
                        </column>
                        <column className='is-8'>
                            <p><code className='prettyprint'>is-wrap</code></p>
                            <p>
                                This one is important. Say you want a row with two columns that are 4/12 and 8/12 on
                                desktop and tablet but 12/12 and 12/12 on mobile, or four columns that are 3/12 on
                                desktop and 6/12 on tablet and mobile. Normally a row will evenly
                                distribute columns. When this class is added to the row, once the column widths
                                reaches 12, it will automatically add the next column below the others.
                            </p>
                            <small>
                                To see the examples in action, resize your browser window or use your browsers
                                responsive inspector.
                            </small>
                            <br />
                            <br />
                            Examples:
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row className="is-wrap"&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-4 is-12-mobile"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-8 is-12-mobile"&gt;column 2&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row className='is-wrap'>
                                    <column className='is-4 is-12-mobile'>column 1</column>
                                    <column className='is-8 is-12-mobile'>column 1</column>
                                </row>
                            </div>
                            <br />
                            <code className='prettyprint lang-html'>
                                &lt;row className="is-wrap"&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3 is-6-tablet is-6-mobile"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3 is-6-tablet is-6-mobile"&gt;column 2&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3 is-6-tablet is-6-mobile"&gt;column 3&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3 is-6-tablet is-6-mobile"&gt;column 4&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row className='is-wrap'>
                                    <column className='is-3 is-6-tablet is-6-mobile'>column 1</column>
                                    <column className='is-3 is-6-tablet is-6-mobile'>column 2</column>
                                    <column className='is-3 is-6-tablet is-6-mobile'>column 3</column>
                                    <column className='is-3 is-6-tablet is-6-mobile'>column 4</column>
                                </row>
                            </div>
                            <br />
                            <hr />
                            <br />
                            <p><code className='prettyprint'>is-justified-center</code></p>
                            <p>
                                By default, a row will justify the columns at the start (left edge) of the row. Adding
                                this class will justify the columns in the center of the row.
                            </p>
                            <code className='prettyprint lang-html'>
                                &lt;row className="is-justified-center"&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3"&gt;column 2&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row className='is-justified-center'>
                                    <column className='is-3'>column 1</column>
                                    <column className='is-3'>column 2</column>
                                </row>
                            </div>
                            <br />
                            <hr />
                            <br />
                            <p><code className='prettyprint'>is-justified-end</code></p>
                            <p>
                                By default, a row will justify the columns at the start (left edge) of the row. Adding
                                this class will justify the columns at the end (right edge).
                            </p>
                            <code className='prettyprint lang-html'>
                                &lt;row className="is-justified-end"&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3"&gt;column 1&lt;/column&gt;
                                <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;&lt;column className="is-3"&gt;column 2&lt;/column&gt;
                                <br />
                                &lt;/row&gt;
                            </code>
                            <br />
                            <br />
                            <div className='grid-demo'>
                                <row className='is-justified-end'>
                                    <column className='is-3'>column 1</column>
                                    <column className='is-3'>column 2</column>
                                </row>
                            </div>
                        </column>
                    </row>
                </fieldset>
            </section>
        );
    }
}

export default connect()(Component);
/* eslint-enable max-len */
